import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { CORK_BOARD_API, CORK_BOARD_CACHE_TAGS, CORK_BOARD_PAGINATION } from '@/constants/residents/corkBoardConstants';
import { trackDeletedThread } from '@/store/slices/residents/corkBoardUISlice';
import type {
  CorkBoardTag,
  CorkBoardNotification,
  ThreadsListResponse,
  ThreadDetailResponse,
  GetThreadsParams,
  GetThreadParams,
  CreateThreadRequest,
  UpdateThreadRequest,
  CreateReplyRequest,
  UpdateReplyRequest,
  DeleteReplyRequest,
  ReportContentRequest,
  GetUserThreadsParams,
  ToggleSaveResponse,
  GetAdminTagsParams,
  CreateTagRequest,
  UpdateTagRequest,
  ReorderTagsRequest,
  GetAdminThreadsParams,
  PinThreadRequest,
  LockThreadRequest,
  CreateAnnouncementRequest,
  ReportsListResponse,
  GetAdminReportsParams,
  UpdateReportStatusRequest,
  CreateSurveyThreadRequest,
  SurveyResultsResponse,
  SubmitSurveyResponseRequest,
  UpdateSurveyConfigRequest,
  GetSurveyResponsesParams,
  SurveyResponsesListResponse,
} from '@/types/residents/corkBoard';

// ============================================================================
// API Response Envelope
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================================================
// File Attachment Type (cross-platform)
// ============================================================================

interface FileAttachment {
  uri: string;
  type: string;
  name: string;
  file?: File;
}

// ============================================================================
// FormData Helpers
// ============================================================================

function buildThreadFormData(data: CreateThreadRequest | CreateSurveyThreadRequest): FormData {
  const formData = new FormData();
  formData.append('propertyId', String(data.propertyId));
  formData.append('title', data.title);
  // body is optional for surveys; omit it entirely when empty.
  if (data.body && data.body.trim()) {
    formData.append('body', data.body);
  }
  formData.append('tagIds', JSON.stringify(data.tagIds));

  if ('kind' in data && data.kind === 'survey') {
    formData.append('kind', 'survey');
    formData.append('survey', JSON.stringify(data.survey));
  }

  if (data.files && data.files.length > 0) {
    appendFiles(formData, data.files);
  }
  return formData;
}

function buildUpdateThreadFormData(data: Omit<UpdateThreadRequest, 'threadId'>): FormData {
  const formData = new FormData();
  if (data.title !== undefined) formData.append('title', data.title);
  if (data.body !== undefined) formData.append('body', data.body);
  if (data.tagIds !== undefined) formData.append('tagIds', JSON.stringify(data.tagIds));
  if (data.removedFileIds && data.removedFileIds.length > 0) {
    formData.append('removedFileIds', JSON.stringify(data.removedFileIds));
  }
  if (data.files && data.files.length > 0) {
    appendFiles(formData, data.files);
  }
  return formData;
}

function buildReplyFormData(body: string, files?: FileAttachment[]): FormData {
  const formData = new FormData();
  formData.append('body', body);

  if (files && files.length > 0) {
    appendFiles(formData, files);
  }
  return formData;
}

function appendFiles(formData: FormData, files: (File | FileAttachment)[]): void {
  files.forEach((file) => {
    if ('file' in file && file.file) {
      formData.append('files', file.file);
    } else if ('uri' in file && file.uri) {
      formData.append('files', {
        uri: file.uri,
        type: file.type || 'application/octet-stream',
        name: file.name || 'file',
      } as any);
    } else {
      formData.append('files', file as File);
    }
  });
}

// ============================================================================
// Optimistic Update Helper
// ============================================================================

/**
 * Centralises the cache-key parsing used by optimistic updates. All four
 * `onQueryStarted` handlers need to iterate cached query entries, extract the
 * original args from the serialised key, and apply a draft updater. Keeping
 * the fragile key-slicing in one place makes it easier to adapt if RTK Query
 * changes its internal serialisation format.
 */
function patchMatchingQueries<TData>(
  state: any,
  dispatch: any,
  endpointName: string,
  matchFn: ((data: TData) => boolean) | undefined,
  updater: (draft: TData) => void,
): { undo: () => void }[] {
  const patches: { undo: () => void }[] = [];
  const queries = state[corkBoardApi.reducerPath]?.queries ?? {};
  for (const [key, entry] of Object.entries(queries)) {
    if (!key.startsWith(`${endpointName}(`) || !(entry as any)?.data) continue;
    if (matchFn && !matchFn((entry as any).data)) continue;
    try {
      const argsJson = key.slice(endpointName.length + 1, -1);
      const originalArgs = JSON.parse(argsJson);
      patches.push(
        dispatch(corkBoardApi.util.updateQueryData(endpointName as any, originalArgs, updater as any)),
      );
    } catch { /* skip malformed keys */ }
  }
  return patches;
}

// ============================================================================
// RTK Query API
// ============================================================================

export const corkBoardApi = createApi({
  reducerPath: 'corkBoardApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(CORK_BOARD_CACHE_TAGS),
  endpoints: (builder) => ({
    // ── Tags ───────────────────────────────────────────────────────────────

    getTags: builder.query<CorkBoardTag[], { propertyId: number }>({
      query: ({ propertyId }) => ({
        url: CORK_BOARD_API.tags.base,
        method: 'GET',
        params: { propertyId },
      }),
      transformResponse: (response: ApiResponse<CorkBoardTag[]> | null) => {
        if (!response?.data) return [];
        return response.data;
      },
      providesTags: [{ type: CORK_BOARD_CACHE_TAGS.Tags, id: 'LIST' }],
    }),

    // ── Threads List (Feed) ────────────────────────────────────────────────

    getThreads: builder.query<ThreadsListResponse, GetThreadsParams>({
      query: ({ tagIds, ...rest }) => ({
        url: CORK_BOARD_API.threads.base,
        method: 'GET',
        params: {
          ...rest,
          ...(tagIds && tagIds.length > 0 ? { tagIds: tagIds.join(',') } : {}),
        },
      }),
      transformResponse: (response: ApiResponse<ThreadsListResponse> | null) => {
        if (!response?.data) {
          return { threads: [], announcements: [], pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 } };
        }
        return response.data;
      },
      providesTags: (result) =>
        result?.threads
          ? [
              ...result.threads.map(({ id }) => ({ type: CORK_BOARD_CACHE_TAGS.Threads, id })),
              { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
            ]
          : [{ type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' }],
    }),

    // ── Thread Detail ──────────────────────────────────────────────────────

    getThread: builder.query<ThreadDetailResponse, GetThreadParams>({
      query: ({ threadId, page, pageSize }) => ({
        url: CORK_BOARD_API.threads.byId(threadId),
        method: 'GET',
        params: { page, pageSize },
      }),
      transformResponse: (response: ApiResponse<ThreadDetailResponse> | null) => {
        if (!response?.data) {
          return {
            thread: {} as ThreadDetailResponse['thread'],
            replies: [],
            pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 },
          };
        }
        return response.data;
      },
      providesTags: (result) =>
        result?.thread?.id
          ? [{ type: CORK_BOARD_CACHE_TAGS.Thread, id: result.thread.id }]
          : [],
    }),

    // ── Create Thread ──────────────────────────────────────────────────────

    createThread: builder.mutation<ThreadDetailResponse | null, CreateThreadRequest | CreateSurveyThreadRequest>({
      query: (data) => ({
        url: CORK_BOARD_API.threads.base,
        method: 'POST',
        body: buildThreadFormData(data),
        formData: true,
      }),
      transformResponse: (response: ApiResponse<ThreadDetailResponse> | null) => {
        return response?.data ?? null;
      },
      // Warm the getThread cache with the detail returned by POST so that when
      // ThreadDetailScreen mounts right after creation, useGetThreadQuery
      // finds a cache hit and skips the loading skeleton.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.thread?.id) return;
          dispatch(
            corkBoardApi.util.upsertQueryData(
              'getThread',
              {
                threadId: String(data.thread.id),
                page: CORK_BOARD_PAGINATION.defaultPage,
                pageSize: CORK_BOARD_PAGINATION.defaultPageSize,
              },
              data,
            ),
          );
        } catch {
          // mutation failed — nothing to prime
        }
      },
      invalidatesTags: [
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.MyThreads, id: 'LIST' },
      ],
    }),

    // ── Update Thread ──────────────────────────────────────────────────────

    updateThread: builder.mutation<void, UpdateThreadRequest>({
      query: ({ threadId, ...data }) => ({
        url: CORK_BOARD_API.threads.byId(threadId),
        method: 'PATCH',
        body: buildUpdateThreadFormData(data),
        formData: true,
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.MyThreads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.SavedThreads, id: 'LIST' },
      ],
    }),

    // ── Delete Thread ──────────────────────────────────────────────────────

    deleteThread: builder.mutation<void, string>({
      query: (threadId) => ({
        url: CORK_BOARD_API.threads.byId(threadId),
        method: 'DELETE',
      }),
      async onQueryStarted(threadId, { dispatch, queryFulfilled, getState }) {
        dispatch(trackDeletedThread(String(threadId)));

        const state = getState() as any;
        const removeThread = (draft: ThreadsListResponse) => {
          draft.threads = draft.threads.filter((t) => String(t.id) !== String(threadId));
          draft.announcements = draft.announcements.filter((t) => String(t.id) !== String(threadId));
        };
        const patches = [
          ...patchMatchingQueries<ThreadsListResponse>(state, dispatch, 'getThreads', undefined, removeThread),
          ...patchMatchingQueries<ThreadsListResponse>(state, dispatch, 'getAdminThreads', undefined, removeThread),
        ];

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: [
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.MyThreads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.SavedThreads, id: 'LIST' },
      ],
    }),

    // ── Create Reply ───────────────────────────────────────────────────────

    createReply: builder.mutation<{ id: string } | null, CreateReplyRequest>({
      query: ({ threadId, body, files }) => ({
        url: CORK_BOARD_API.threads.replies(threadId),
        method: 'POST',
        body: buildReplyFormData(body, files as FileAttachment[]),
        formData: true,
      }),
      transformResponse: (response: ApiResponse<{ id: string }> | null) => {
        return response?.data ?? null;
      },
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
      ],
    }),

    // ── Update Reply ───────────────────────────────────────────────────────

    updateReply: builder.mutation<void, UpdateReplyRequest>({
      query: ({ replyId, body }) => ({
        url: CORK_BOARD_API.replies.byId(replyId),
        method: 'PATCH',
        body: { body },
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
      ],
    }),

    // ── Delete Reply ───────────────────────────────────────────────────────

    deleteReply: builder.mutation<void, DeleteReplyRequest>({
      query: ({ replyId }) => ({
        url: CORK_BOARD_API.replies.byId(replyId),
        method: 'DELETE',
      }),
      async onQueryStarted({ replyId, threadId }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;
        const patches = patchMatchingQueries<ThreadDetailResponse>(
          state, dispatch, 'getThread',
          (data) => !!data.thread && String(data.thread.id) === String(threadId),
          (draft) => {
            draft.replies = draft.replies.filter((r) => String(r.id) !== String(replyId));
            if (draft.thread) {
              draft.thread.replyCount = Math.max(0, draft.thread.replyCount - 1);
            }
          },
        );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
      ],
    }),

    // ── Toggle Save Thread ─────────────────────────────────────────────────

    toggleSaveThread: builder.mutation<ToggleSaveResponse | null, string>({
      query: (threadId) => ({
        url: CORK_BOARD_API.threads.save(threadId),
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<ToggleSaveResponse> | null) => {
        return response?.data ?? null;
      },
      async onQueryStarted(threadId, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;

        const detailPatches = patchMatchingQueries<ThreadDetailResponse>(
          state, dispatch, 'getThread',
          (data) => !!data.thread && String(data.thread.id) === String(threadId),
          (draft) => {
            if (draft?.thread) {
              draft.thread.isSaved = !draft.thread.isSaved;
            }
          },
        );

        const listPatches = patchMatchingQueries<ThreadsListResponse>(
          state, dispatch, 'getThreads', undefined, (draft) => {
            const thread = draft.threads.find((t) => String(t.id) === String(threadId));
            if (thread) thread.isSaved = !thread.isSaved;
            const ann = draft.announcements.find((t) => String(t.id) === String(threadId));
            if (ann) ann.isSaved = !ann.isSaved;
          },
        );

        const myPatches = patchMatchingQueries<ThreadsListResponse>(
          state, dispatch, 'getMyThreads', undefined, (draft) => {
            const thread = draft.threads.find((t) => String(t.id) === String(threadId));
            if (thread) thread.isSaved = !thread.isSaved;
          },
        );

        const savedPatches = patchMatchingQueries<ThreadsListResponse>(
          state, dispatch, 'getSavedThreads', undefined, (draft) => {
            draft.threads = draft.threads.filter((t) => String(t.id) !== String(threadId));
          },
        );

        const patches = [...detailPatches, ...listPatches, ...myPatches, ...savedPatches];

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (_result, _error, threadId) => [
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.SavedThreads, id: 'LIST' },
      ],
    }),

    // ── Record View ──────────────────────────────────────────────────────

    recordThreadView: builder.mutation<void, string>({
      query: (threadId) => ({
        url: CORK_BOARD_API.threads.view(threadId),
        method: 'POST',
      }),
    }),

    // ── Newsfeed Visit Tracking ────────────────────────────────────────────
    // Powers the home Quick Actions "Newsfeed" badge: threads posted since the
    // resident last opened the board.

    getNewsfeedUnseenCount: builder.query<number, { propertyId: number }>({
      query: ({ propertyId }) => ({
        url: CORK_BOARD_API.feed.unseenCount,
        method: 'GET',
        params: { propertyId },
      }),
      transformResponse: (response: ApiResponse<{ count: number }> | null) =>
        response?.data?.count ?? 0,
      providesTags: [{ type: CORK_BOARD_CACHE_TAGS.FeedUnseen, id: 'COUNT' }],
    }),

    markNewsfeedSeen: builder.mutation<void, { propertyId: number }>({
      query: ({ propertyId }) => ({
        url: CORK_BOARD_API.feed.seen,
        method: 'POST',
        body: { propertyId },
      }),
      // Zero the badge immediately; the invalidation below re-syncs with the
      // server once the stamp lands (and reverts if it failed).
      async onQueryStarted({ propertyId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          corkBoardApi.util.updateQueryData('getNewsfeedUnseenCount', { propertyId }, () => 0),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: CORK_BOARD_CACHE_TAGS.FeedUnseen, id: 'COUNT' }],
    }),

    // ── My Threads ─────────────────────────────────────────────────────────

    getMyThreads: builder.query<ThreadsListResponse, GetUserThreadsParams>({
      query: (params) => ({
        url: CORK_BOARD_API.myThreads,
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<ThreadsListResponse> | null) => {
        if (!response?.data) {
          return { threads: [], announcements: [], pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 } };
        }
        return response.data;
      },
      providesTags: [{ type: CORK_BOARD_CACHE_TAGS.MyThreads, id: 'LIST' }],
    }),

    // ── Saved Threads ──────────────────────────────────────────────────────

    getSavedThreads: builder.query<ThreadsListResponse, GetUserThreadsParams>({
      query: (params) => ({
        url: CORK_BOARD_API.saved,
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<ThreadsListResponse> | null) => {
        if (!response?.data) {
          return { threads: [], announcements: [], pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 } };
        }
        return response.data;
      },
      providesTags: [{ type: CORK_BOARD_CACHE_TAGS.SavedThreads, id: 'LIST' }],
    }),

    // ── Report Content ─────────────────────────────────────────────────────

    reportContent: builder.mutation<{ id: string } | null, ReportContentRequest>({
      query: (data) => ({
        url: CORK_BOARD_API.reports.base,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<{ id: string }> | null) => {
        return response?.data ?? null;
      },
    }),

    // ── Cork Board Notifications (warnings, etc.) ────────────────────────

    getCorkBoardNotifications: builder.query<CorkBoardNotification[], void>({
      query: () => ({
        url: CORK_BOARD_API.notifications.base,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<CorkBoardNotification[]> | null): CorkBoardNotification[] => {
        if (!response?.data) return [];
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: CORK_BOARD_CACHE_TAGS.Notifications, id })),
              { type: CORK_BOARD_CACHE_TAGS.Notifications, id: 'LIST' },
            ]
          : [{ type: CORK_BOARD_CACHE_TAGS.Notifications, id: 'LIST' }],
    }),

    markCorkBoardNotificationAsRead: builder.mutation<void, string>({
      query: (id) => ({
        url: CORK_BOARD_API.notifications.markAsRead(id),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: CORK_BOARD_CACHE_TAGS.Notifications, id },
        { type: CORK_BOARD_CACHE_TAGS.Notifications, id: 'LIST' },
      ],
    }),

    deleteCorkBoardNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: CORK_BOARD_API.notifications.delete(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: CORK_BOARD_CACHE_TAGS.Notifications, id },
        { type: CORK_BOARD_CACHE_TAGS.Notifications, id: 'LIST' },
      ],
    }),

    // ════════════════════════════════════════════════════════════════════════
    // Surveys
    // ════════════════════════════════════════════════════════════════════════

    getSurveyResults: builder.query<SurveyResultsResponse, string>({
      query: (threadId) => ({
        url: CORK_BOARD_API.threads.surveyResults(threadId),
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<SurveyResultsResponse> | null) => {
        if (!response?.data) {
          return {
            surveyId: 0,
            isAnonymous: false,
            allowReplies: false,
            expiresAt: null,
            closedAt: null,
            responseCount: 0,
            hasResponded: false,
            isClosed: false,
            questions: [],
          };
        }
        return response.data;
      },
      providesTags: (_result, _error, threadId) => [
        { type: CORK_BOARD_CACHE_TAGS.SurveyResults, id: threadId },
      ],
    }),

    submitSurveyResponse: builder.mutation<SurveyResultsResponse | null, SubmitSurveyResponseRequest>({
      query: ({ threadId, answers }) => ({
        url: CORK_BOARD_API.threads.surveyResponses(threadId),
        method: 'POST',
        body: { answers },
      }),
      transformResponse: (response: ApiResponse<SurveyResultsResponse> | null) => {
        return response?.data ?? null;
      },
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.SurveyResults, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
      ],
    }),

    updateSurveyConfig: builder.mutation<void, UpdateSurveyConfigRequest>({
      query: ({ threadId, ...body }) => ({
        url: CORK_BOARD_API.threads.survey(threadId),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.SurveyResults, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
      ],
    }),

    closeSurvey: builder.mutation<void, string>({
      query: (threadId) => ({
        url: CORK_BOARD_API.threads.surveyClose(threadId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, threadId) => [
        { type: CORK_BOARD_CACHE_TAGS.SurveyResults, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
      ],
    }),

    getSurveyResponses: builder.query<SurveyResponsesListResponse, GetSurveyResponsesParams>({
      query: ({ threadId, page = 0, pageSize = 20 }) => ({
        url: CORK_BOARD_API.threads.surveyResponses(threadId),
        method: 'GET',
        params: { page, pageSize },
      }),
      transformResponse: (response: ApiResponse<SurveyResponsesListResponse> | null) => {
        if (!response?.data) {
          return {
            responses: [],
            pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 },
          };
        }
        return response.data;
      },
      providesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.SurveyResponses, id: threadId },
      ],
    }),

    // ════════════════════════════════════════════════════════════════════════
    // Admin Tag Management
    // ════════════════════════════════════════════════════════════════════════

    getAdminTags: builder.query<CorkBoardTag[], GetAdminTagsParams>({
      query: ({ propertyId, includeDisabled = true }) => ({
        url: CORK_BOARD_API.tags.base,
        method: 'GET',
        params: { propertyId, includeDisabled },
      }),
      transformResponse: (response: ApiResponse<CorkBoardTag[]> | null) => {
        if (!response?.data) return [];
        return response.data;
      },
      providesTags: [{ type: CORK_BOARD_CACHE_TAGS.Tags, id: 'LIST' }],
    }),

    createTag: builder.mutation<CorkBoardTag | null, CreateTagRequest>({
      query: (data) => ({
        url: CORK_BOARD_API.tags.base,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<CorkBoardTag> | null) => {
        return response?.data ?? null;
      },
      invalidatesTags: [{ type: CORK_BOARD_CACHE_TAGS.Tags, id: 'LIST' }],
    }),

    updateTag: builder.mutation<CorkBoardTag | null, UpdateTagRequest>({
      query: ({ tagId, ...data }) => ({
        url: CORK_BOARD_API.tags.byId(tagId),
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: ApiResponse<CorkBoardTag> | null) => {
        return response?.data ?? null;
      },
      async onQueryStarted({ tagId, ...data }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;
        const applyUpdate = (draft: CorkBoardTag[]) => {
          const tag = draft.find((t) => t.id === tagId);
          if (!tag) return;
          if (data.name !== undefined) tag.name = data.name;
          if (data.color !== undefined) tag.color = data.color;
          if (data.icon !== undefined) tag.icon = data.icon;
          if (data.isEnabled !== undefined) tag.isEnabled = data.isEnabled;
        };
        const patches = [
          ...patchMatchingQueries<CorkBoardTag[]>(state, dispatch, 'getAdminTags', undefined, applyUpdate),
          ...patchMatchingQueries<CorkBoardTag[]>(state, dispatch, 'getTags', undefined, applyUpdate),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    deleteTag: builder.mutation<void, number>({
      query: (tagId) => ({
        url: CORK_BOARD_API.tags.byId(tagId),
        method: 'DELETE',
      }),
      async onQueryStarted(tagId, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;
        const applyDelete = (draft: CorkBoardTag[]) => {
          const index = draft.findIndex((t) => t.id === tagId);
          if (index !== -1) draft.splice(index, 1);
        };
        const patches = [
          ...patchMatchingQueries<CorkBoardTag[]>(state, dispatch, 'getAdminTags', undefined, applyDelete),
          ...patchMatchingQueries<CorkBoardTag[]>(state, dispatch, 'getTags', undefined, applyDelete),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    reorderTags: builder.mutation<void, ReorderTagsRequest>({
      query: (data) => ({
        url: CORK_BOARD_API.tags.reorder,
        method: 'POST',
        body: data,
      }),
      async onQueryStarted({ tagIds }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;
        const applyReorder = (draft: CorkBoardTag[]) => {
          const positionMap = new Map(tagIds.map((id, idx) => [id, idx]));
          draft.forEach((tag) => {
            const newPos = positionMap.get(tag.id);
            if (newPos !== undefined) tag.position = newPos;
          });
          draft.sort((a, b) => a.position - b.position);
        };
        const patches = [
          ...patchMatchingQueries<CorkBoardTag[]>(state, dispatch, 'getAdminTags', undefined, applyReorder),
          ...patchMatchingQueries<CorkBoardTag[]>(state, dispatch, 'getTags', undefined, applyReorder),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    // ════════════════════════════════════════════════════════════════════════
    // Admin Thread Moderation
    // ════════════════════════════════════════════════════════════════════════

    getAdminThreads: builder.query<ThreadsListResponse, GetAdminThreadsParams>({
      query: ({ tagIds, ...rest }) => ({
        url: CORK_BOARD_API.threads.base,
        method: 'GET',
        params: {
          ...rest,
          ...(tagIds && tagIds.length > 0 ? { tagIds: tagIds.join(',') } : {}),
        },
      }),
      transformResponse: (response: ApiResponse<ThreadsListResponse> | null) => {
        if (!response?.data) {
          return { threads: [], announcements: [], pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 } };
        }
        return response.data;
      },
      providesTags: (result) => {
        const tags: { type: typeof CORK_BOARD_CACHE_TAGS.Threads; id: string }[] = [
          { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
        ];
        if (result?.threads) {
          result.threads.forEach(({ id }) => tags.push({ type: CORK_BOARD_CACHE_TAGS.Threads, id }));
        }
        if (result?.announcements) {
          result.announcements.forEach(({ id }) => tags.push({ type: CORK_BOARD_CACHE_TAGS.Threads, id }));
        }
        return tags;
      },
    }),

    pinThread: builder.mutation<void, PinThreadRequest>({
      query: ({ threadId, isPinned }) => ({
        url: CORK_BOARD_API.threads.pin(threadId),
        method: 'POST',
        body: { isPinned },
      }),
      async onQueryStarted({ threadId, isPinned }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;
        const applyPin = (draft: ThreadsListResponse) => {
          const thread = draft.threads.find((t) => String(t.id) === String(threadId));
          if (thread) thread.isPinned = isPinned;
          const ann = draft.announcements.find((t) => String(t.id) === String(threadId));
          if (ann) ann.isPinned = isPinned;
        };
        const patches = [
          ...patchMatchingQueries<ThreadsListResponse>(state, dispatch, 'getThreads', undefined, applyPin),
          ...patchMatchingQueries<ThreadsListResponse>(state, dispatch, 'getAdminThreads', undefined, applyPin),
        ];
        try { await queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
      },
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
      ],
    }),

    lockThread: builder.mutation<void, LockThreadRequest>({
      query: ({ threadId, isLocked }) => ({
        url: CORK_BOARD_API.threads.lock(threadId),
        method: 'POST',
        body: { isLocked },
      }),
      async onQueryStarted({ threadId, isLocked }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any;
        const applyLock = (draft: ThreadsListResponse) => {
          const thread = draft.threads.find((t) => String(t.id) === String(threadId));
          if (thread) thread.isLocked = isLocked;
          const ann = draft.announcements.find((t) => String(t.id) === String(threadId));
          if (ann) ann.isLocked = isLocked;
        };
        const patches = [
          ...patchMatchingQueries<ThreadsListResponse>(state, dispatch, 'getThreads', undefined, applyLock),
          ...patchMatchingQueries<ThreadsListResponse>(state, dispatch, 'getAdminThreads', undefined, applyLock),
        ];
        try { await queryFulfilled; } catch { patches.forEach((p) => p.undo()); }
      },
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: threadId },
        { type: CORK_BOARD_CACHE_TAGS.Thread, id: threadId },
      ],
    }),

    createAnnouncement: builder.mutation<{ id: string } | null, CreateAnnouncementRequest>({
      query: (data) => ({
        url: CORK_BOARD_API.announcements.base,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<{ id: string }> | null) => {
        return response?.data ?? null;
      },
      invalidatesTags: [
        { type: CORK_BOARD_CACHE_TAGS.Threads, id: 'LIST' },
      ],
    }),

    // ════════════════════════════════════════════════════════════════════════
    // Admin Report Review
    // ════════════════════════════════════════════════════════════════════════

    getAdminReports: builder.query<ReportsListResponse, GetAdminReportsParams>({
      query: ({ status, ...rest }) => ({
        url: CORK_BOARD_API.reports.base,
        method: 'GET',
        params: {
          ...rest,
          ...(status ? { status } : {}),
        },
      }),
      transformResponse: (response: ApiResponse<ReportsListResponse> | null) => {
        if (!response?.data) {
          return { reports: [], pagination: { currentPage: 0, pageSize: 20, totalPages: 0, totalCount: 0 } };
        }
        return response.data;
      },
      providesTags: (result) => {
        const tags: { type: typeof CORK_BOARD_CACHE_TAGS.Reports; id: string }[] = [
          { type: CORK_BOARD_CACHE_TAGS.Reports, id: 'LIST' },
        ];
        if (result?.reports) {
          result.reports.forEach(({ id }) => tags.push({ type: CORK_BOARD_CACHE_TAGS.Reports, id }));
        }
        return tags;
      },
    }),

    updateReportStatus: builder.mutation<{ id: string; status: string; resolution: string | null; reviewedBy: number; reviewedAt: string } | null, UpdateReportStatusRequest>({
      query: ({ reportId, status, resolution }) => ({
        url: CORK_BOARD_API.reports.byId(reportId),
        method: 'PATCH',
        body: { status, ...(resolution ? { resolution } : {}) },
      }),
      transformResponse: (response: ApiResponse<{ id: string; status: string; resolution: string | null; reviewedBy: number; reviewedAt: string }> | null) => {
        return response?.data ?? null;
      },
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: CORK_BOARD_CACHE_TAGS.Reports, id: 'LIST' },
        { type: CORK_BOARD_CACHE_TAGS.Reports, id: reportId },
      ],
    }),
  }),
});

// ============================================================================
// Exported Hooks
// ============================================================================

export const {
  useGetTagsQuery,
  useGetThreadsQuery,
  useGetThreadQuery,
  useCreateThreadMutation,
  useUpdateThreadMutation,
  useDeleteThreadMutation,
  useCreateReplyMutation,
  useUpdateReplyMutation,
  useDeleteReplyMutation,
  useToggleSaveThreadMutation,
  useRecordThreadViewMutation,
  useGetNewsfeedUnseenCountQuery,
  useMarkNewsfeedSeenMutation,
  useGetMyThreadsQuery,
  useGetSavedThreadsQuery,
  useReportContentMutation,
  // Surveys
  useGetSurveyResultsQuery,
  useSubmitSurveyResponseMutation,
  useUpdateSurveyConfigMutation,
  useCloseSurveyMutation,
  useGetSurveyResponsesQuery,
  // Cork Board Notifications
  useGetCorkBoardNotificationsQuery,
  useMarkCorkBoardNotificationAsReadMutation,
  useDeleteCorkBoardNotificationMutation,
  // Admin Tag Management
  useGetAdminTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useReorderTagsMutation,
  // Admin Thread Moderation
  useGetAdminThreadsQuery,
  usePinThreadMutation,
  useLockThreadMutation,
  useCreateAnnouncementMutation,
  // Admin Report Review
  useGetAdminReportsQuery,
  useUpdateReportStatusMutation,
} = corkBoardApi;
